import {
  Button,
  Icon,
  Loading,
  StepperBody,
  StepperHeader,
  type Step,
} from "@uigovpe/components";
import { useEffect, useMemo, useRef, useState } from "react";
import { FormProvider } from "react-hook-form";
import { useProfileForm } from "@/hooks/profile/useProfileForm";
import type { ProfileField } from "../../types";
import { stepFields } from "./constants";
import ProfileStepOne from "./steps/profile-step-one";
import ProfileStepTwo from "./steps/profile-step-two";
import ProfileStepThree from "./steps/profile-step-three";
import ProfileStepReview from "./steps/profile-step-review";
import { useSearchParams } from "react-router-dom";

type ProfileStepperProps = {
  onLoadingChange?: (isLoading: boolean) => void;
};

export default function ProfileStepper({
  onLoadingChange,
}: ProfileStepperProps) {
  const [searchParams] = useSearchParams();
  const {
    categories,
    form,
    hasExistingBusiness,
    isLoading,
    isSaving,
    publish,
    saveDraft,
  } = useProfileForm();
  const requestedSection = searchParams.get("secao");
  const requestedStep =
    {
      "dados-pessoais": 0,
      "dados-negocio": 1,
      "contatos-redes-sociais": 1,
      "endereco-atendimento": 2,
    }[requestedSection ?? ""] ?? 0;
  const [currentStep, setCurrentStep] = useState(requestedStep);
  const [completedSteps, setCompletedSteps] = useState(() =>
    Array.from({ length: 4 }, (_, index) => index < requestedStep),
  );
  const stepperRef = useRef<HTMLFormElement>(null);

  const steps: Step[] = useMemo(
    () => [
      {
        label: "Etapa 1",
        title: "Dados da empreendedora",
        icon: <Icon icon="person" />,
        render: () => (
          <div className="mb-15! flex! w-full! flex-col!">
            <ProfileStepOne />
          </div>
        ),
        isCompleted: completedSteps[0],
      },
      {
        label: "Etapa 2",
        title: "Dados do negócio",
        icon: <Icon icon="business_center" outline />,
        render: () => (
          <div className="mb-15! flex! w-full! flex-col!">
            <ProfileStepTwo
              categories={categories}
              isLoadingCategories={isLoading}
            />
          </div>
        ),
        isCompleted: completedSteps[1],
      },
      {
        label: "Etapa 3",
        title: "Endereço e atendimento",
        icon: <Icon icon="location_on" outline />,
        render: () => (
          <div className="mb-15! flex! w-full! flex-col!">
            <ProfileStepThree />
          </div>
        ),
        isCompleted: completedSteps[2],
      },
      {
        label: "Etapa 4",
        title: "Revisão e envio",
        icon: <Icon icon="search" outline />,
        render: () => (
          <div className="mb-15! flex! w-full! flex-col!">
            <ProfileStepReview categories={categories} />
          </div>
        ),
        isConclusion: true,
        isCompleted: completedSteps[3],
      },
    ],
    [categories, completedSteps, isLoading],
  );

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  useEffect(() => {
    setCurrentStep(requestedStep);
    setCompletedSteps(
      Array.from({ length: 4 }, (_, index) => index < requestedStep),
    );
  }, [requestedStep]);

  const scrollToTop = () => {
    requestAnimationFrame(() => {
      const container =
        stepperRef.current?.closest<HTMLElement>(".overflow-y-auto");
      if (container) container.scrollTo({ top: 0, behavior: "smooth" });
      else
        stepperRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
    });
  };

  const validationFields = () => {
    const fields = [...stepFields[currentStep]];
    const remove = (field: ProfileField) => {
      const index = fields.indexOf(field);
      if (index >= 0) fields.splice(index, 1);
    };
    if (!form.getValues("isDisabledPerson")) remove("disabilityType");
    if (!form.getValues("participatesInAssociation")) remove("associationName");
    return fields;
  };

  const onNext = async () => {
    if (!(await form.trigger(validationFields()))) return;
    if (currentStep === steps.length - 1) {
      await form.handleSubmit(publish)();
      return;
    }
    setCompletedSteps((current) =>
      current.map((done, index) => (index === currentStep ? true : done)),
    );
    setCurrentStep((step) => step + 1);
    scrollToTop();
  };

  const onSaveDraft = async () => {
    if (!(await form.trigger(validationFields()))) return;
    await saveDraft(currentStep);
  };

  const onPrevious = () => {
    const previousStep = Math.max(currentStep - 1, 0);
    setCompletedSteps((current) =>
      current.map((isCompleted, index) =>
        index >= previousStep ? false : isCompleted,
      ),
    );
    setCurrentStep(previousStep);
    scrollToTop();
  };

  const onChangeStep = (stepIndex: number) => {
    setCompletedSteps((current) =>
      current.map((isCompleted, index) =>
        index >= stepIndex ? false : isCompleted,
      ),
    );
    setCurrentStep(stepIndex);
    scrollToTop();
  };

  if (isLoading) {
    return (
      <div
        className="flex min-h-[320px] w-full items-center justify-center"
        role="status"
        aria-live="polite"
        aria-label="Carregando perfil"
      >
        <Loading />
        <span className="sr-only">Carregando perfil...</span>
      </div>
    );
  }

  return (
    <FormProvider {...form}>
      <form
        ref={stepperRef}
        onSubmit={(event) => event.preventDefault()}
        className="profile-stepper-form"
      >
        <StepperHeader
          steps={steps}
          currentStep={currentStep}
          providerName={steps[currentStep].label}
          processName={String(steps[currentStep].title)}
          onChangeStep={onChangeStep}
          ui={{
            providerName: {
              className: "text-base! leading-6! font-normal!",
            },
            processName: {
              className: "text-3xl! leading-10! font-bold!",
            },
          }}
        />
        <StepperBody
          steps={steps}
          currentStep={currentStep}
          className="border-rounded w-full! min-w-0! rounded-sm! border! border-zinc-200! bg-background! px-4! py-5! shadow-sm! md:px-10!"
          footer={
            <div
              className={`flex! w-full! justify-end! gap-4! max-[479px]:grid! max-[479px]:grid-cols-2! max-[479px]:gap-3! ${
                currentStep === 0 || currentStep === 2 || currentStep === 3
                  ? "max-[479px]:pb-12!"
                  : ""
              }`}
            >
              {currentStep > 0 && (
                <Button
                  label="Voltar"
                  type="button"
                  outlined
                  disabled={isSaving}
                  className={`secondary-action-button min-w-0! max-[479px]:w-full! max-[479px]:justify-center! ${
                    currentStep === 2 || currentStep === 3
                      ? "max-[479px]:col-span-2!"
                      : ""
                  }`}
                  icon={
                    <Icon
                      icon="arrow_left_alt"
                      outline
                      className="text-[#4e3873]!"
                    />
                  }
                  onClick={onPrevious}
                />
              )}
              {currentStep <= 1 && (
                <Button
                  label={isSaving ? "Salvando..." : "Salvar rascunho"}
                  type="button"
                  outlined
                  disabled={isLoading || isSaving}
                  className={`secondary-action-button min-w-0! max-[479px]:w-full! max-[479px]:justify-center! ${
                    currentStep === 0 ? "max-[479px]:col-span-2!" : ""
                  }`}
                  icon={
                    <Icon icon="save" outline className="text-[#4e3873]!" />
                  }
                  onClick={onSaveDraft}
                />
              )}
              <Button
                label={
                  currentStep === 3
                    ? isSaving
                      ? hasExistingBusiness
                        ? "Atualizando..."
                        : "Cadastrando..."
                      : hasExistingBusiness
                        ? "Atualizar perfil"
                        : "Cadastrar perfil"
                    : "Próximo"
                }
                type="button"
                disabled={isSaving || isLoading}
                className="min-w-0! border-[#4e3873]! bg-[#4e3873]! text-white! max-[479px]:col-span-2! max-[479px]:w-full! max-[479px]:justify-center! [&_.p-button-label]:text-white!"
                icon={
                  <Icon
                    icon={
                      currentStep === 3 ? "arrow_outward" : "arrow_right_alt"
                    }
                    outline
                    className="text-white!"
                  />
                }
                onClick={onNext}
              />
            </div>
          }
        />
      </form>
    </FormProvider>
  );
}
